import { useEffect, useMemo } from 'react';
import { t, tscope } from '@i18n/index';
import { useDocumentsViewContext } from '@views/ProofDocuments/hooks/useDocumentsContext';
import {
  useCancelRequisitionMutation,
  useCancelReasonsQuery,
} from '../graphql';
import { useCancelRequisitionForm } from './useCancelRequisitionForm';

export const ts = tscope('Documents.cancelRequisitionModal');

const REASON_ORDER = [
  'CANCEL_BY_MISSING_INCOME_DOC',
  'CANCEL_BY_MISSING_ADDRESS_DOC',
  'CANCEL_BY_UNCONFIDENT',
  'CANCEL_BY_TECHNICAL_ISSUES',
  'CANCEL_BY_MORE_DOCUMENT_INFO',
  'CANCEL_BY_INVALID_DOCUMENTS',
  'CANCEL_BY_OTHER_REASON',
];

const buildCancellingReasonOptions = (cancelReasonsData) => {
  if (!cancelReasonsData?.length) return null;
  return REASON_ORDER
    .map((reason) => cancelReasonsData.find(({ reason: r }) => r === reason))
    .map(({ reason }) => ({ value: reason, label: ts(`reasonOptions.${reason}`) }));
};

export const useController = ({ onClose }) => {
  const { activeRequisition } = useDocumentsViewContext();
  const {
    watch,
    control,
    setValue,
    setError,
    createOnSubmit,
  } = useCancelRequisitionForm();
  const [cancelRequisition, { loading: isLoadingCancel }] = useCancelRequisitionMutation({
    onCompleted: () => onClose(),
    onErrorCode: {
      FORBIDDEN_ERROR: () => {
        setValue('password', '');
        setError('password', { message: t('FormResolver.invalidPassword') });
      },
    },
  });
  const { status: requisitionStatus } = activeRequisition || {};
  const [queryCancellingReason, { data: cancelReasonsData }] = useCancelReasonsQuery({
    variables: {
      status: requisitionStatus,
    },
  });
  const onSubmit = createOnSubmit({ cancelRequisition });
  const cancellingReasons = cancelReasonsData?.catalogs?.cancelRequisitionReasons;
  const cancellingReasonOptions = useMemo(
    () => buildCancellingReasonOptions(cancellingReasons),
    [cancellingReasons],
  );

  const displayReasonField = watch('reason') === 'CANCEL_BY_OTHER_REASON';

  useEffect(() => {
    if (requisitionStatus) queryCancellingReason();
  }, [requisitionStatus, queryCancellingReason]);

  return {
    control,
    onSubmit,
    isLoadingCancel,
    displayReasonField,
    cancellingReasonOptions,
  };
};
